/**
 * Security Utilities for NTC Admin Dashboard
 * Provides encryption, secure storage, and protection mechanisms
 */

// Simple encryption/decryption using Base64 (for basic obfuscation)
// Note: For production, consider using crypto-js or Web Crypto API
const encrypt = (text) => {
  if (!text) return null;
  try {
    // Base64 encoding with additional obfuscation
    const encoded = btoa(unescape(encodeURIComponent(text)));
    // Add simple obfuscation layer
    return encoded.split('').reverse().join('');
  } catch (error) {
    console.error('Encryption error:', error);
    return null;
  }
};

const decrypt = (encryptedText) => {
  if (!encryptedText) return null;
  try {
    // Reverse the obfuscation
    const reversed = encryptedText.split('').reverse().join('');
    // Decode from Base64
    return decodeURIComponent(escape(atob(reversed)));
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

// Secure storage wrapper with encryption
export const secureStorage = {
  setItem: (key, value) => {
    try {
      const encrypted = encrypt(JSON.stringify(value));
      if (encrypted) {
        localStorage.setItem(`_sec_${key}`, encrypted);
      }
    } catch (error) {
      console.error('Secure storage set error:', error);
    }
  },

  getItem: (key) => {
    try {
      const encrypted = localStorage.getItem(`_sec_${key}`);
      if (encrypted) {
        const decrypted = decrypt(encrypted);
        return decrypted ? JSON.parse(decrypted) : null;
      }
    } catch (error) {
      console.error('Secure storage get error:', error);
    }
    return null;
  },

  removeItem: (key) => {
    try {
      localStorage.removeItem(`_sec_${key}`);
    } catch (error) {
      console.error('Secure storage remove error:', error);
    }
  },

  clear: () => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('_sec_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Secure storage clear error:', error);
    }
  }
};

// Check if running in production
export const isProduction = () => {
  return process.env.NODE_ENV === 'production';
};

// Secure console wrapper - disables console in production
export const secureConsole = {
  log: (...args) => {
    if (!isProduction()) {
      console.log(...args);
    }
  },
  error: (...args) => {
    // Always log errors, but sanitize sensitive data in production
    if (isProduction()) {
      const sanitized = args.map(arg => {
        if (typeof arg === 'object' && arg !== null) {
          // Remove sensitive fields
          const sanitizedObj = { ...arg };
          delete sanitizedObj.token;
          delete sanitizedObj.password;
          delete sanitizedObj.authorization;
          delete sanitizedObj.login_data;
          return sanitizedObj;
        }
        return arg;
      });
      console.error(...sanitized);
    } else {
      console.error(...args);
    }
  },
  warn: (...args) => {
    if (!isProduction()) {
      console.warn(...args);
    }
  },
  info: (...args) => {
    if (!isProduction()) {
      console.info(...args);
    }
  },
  debug: (...args) => {
    if (!isProduction()) {
      console.debug(...args);
    }
  }
};

// Prevent right-click and common developer tools shortcuts
export const enableProtection = () => {
  if (!isProduction()) return; // Only enable in production

  // Disable right-click
  document.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    return false;
  });

  // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
  document.addEventListener('keydown', (e) => {
    // F12
    if (e.keyCode === 123) {
      e.preventDefault();
      return false;
    }
    // Ctrl+Shift+I (DevTools)
    if (e.ctrlKey && e.shiftKey && e.keyCode === 73) {
      e.preventDefault();
      return false;
    }
    // Ctrl+Shift+J (Console)
    if (e.ctrlKey && e.shiftKey && e.keyCode === 74) {
      e.preventDefault();
      return false;
    }
    // Ctrl+U (View Source)
    if (e.ctrlKey && e.keyCode === 85) {
      e.preventDefault();
      return false;
    }
    // Ctrl+S (Save Page)
    if (e.ctrlKey && e.keyCode === 83) {
      e.preventDefault();
      return false;
    }
  });

  // Disable text selection (optional - can be too restrictive)
  // document.addEventListener('selectstart', (e) => {
  //   e.preventDefault();
  //   return false;
  // });

  // Disable drag and drop of images
  document.addEventListener('dragstart', (e) => {
    if (e.target.tagName === 'IMG') {
      e.preventDefault();
      return false;
    }
  });
};

// Detect and warn about DevTools
export const detectDevTools = () => {
  if (!isProduction()) return;

  let devtools = { open: false, orientation: null };
  const threshold = 160;

  setInterval(() => {
    if (
      window.outerHeight - window.innerHeight > threshold ||
      window.outerWidth - window.innerWidth > threshold
    ) {
      if (!devtools.open) {
        devtools.open = true;
        // Optionally redirect or show warning
        // window.location.href = '/';
        secureConsole.warn('Developer tools detected');
      }
    } else {
      devtools.open = false;
    }
  }, 500);
};

// Prevent data extraction via copy/paste
export const preventDataExtraction = () => {
  if (!isProduction()) return;

  // Disable copy
  document.addEventListener('copy', (e) => {
    e.clipboardData.setData('text/plain', '');
    e.preventDefault();
  });

  // Disable cut
  document.addEventListener('cut', (e) => {
    e.clipboardData.setData('text/plain', '');
    e.preventDefault();
  });

  // Disable print screen (limited effectiveness)
  document.addEventListener('keyup', (e) => {
    if (e.keyCode === 44) { // Print Screen
      e.preventDefault();
    }
  });
};

// Sanitize data before logging or sending
export const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') return data;

  const sanitized = { ...data };
  const sensitiveFields = [
    'token',
    'password',
    'authorization',
    'apiKey',
    'secret',
    'privateKey',
    'accessToken',
    'refreshToken',
    'login_data',
    'creditCard',
    'ssn',
    'bankAccount'
  ];

  sensitiveFields.forEach(field => {
    if (sanitized[field]) {
      sanitized[field] = '***REDACTED***';
    }
  });

  return sanitized;
};

// Watermark data with user info (for tracking)
export const addWatermark = (data, userId) => {
  if (!isProduction()) return data;
  
  return {
    ...data,
    _watermark: {
      userId: userId ? encrypt(userId.toString()) : null,
      timestamp: Date.now(),
      sessionId: encrypt(Math.random().toString(36).substring(7))
    }
  };
};

// Clear sensitive data from memory
export const clearSensitiveData = () => {
  // Clear localStorage items that might contain sensitive data
  const sensitiveKeys = ['login_data', 'token', 'user_data'];
  sensitiveKeys.forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Clear secure storage
  secureStorage.clear();
};

export default {
  encrypt,
  decrypt,
  secureStorage,
  secureConsole,
  enableProtection,
  detectDevTools,
  preventDataExtraction,
  sanitizeData,
  addWatermark,
  clearSensitiveData,
  isProduction
};

